import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Settings as SettingsIcon, Save, AlertCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { getApiClient } from '../lib/api'
import LoadingButton from '../components/LoadingButton'
import { useModalScrollLock } from '../hooks/useModalScrollLock'
import { optionalPhoneSchema } from '../lib/phoneValidation'

const settingsValidation = z.object({
  // General Information
  websiteName: z.string()
    .min(2, 'Website name must be at least 2 characters')
    .max(100, 'Website name must not exceed 100 characters')
    .default(''),
  tagline: z.string()
    .max(160, 'Tagline must not exceed 160 characters')
    .default(''),
  contactEmail: z.string()
    .refine((val) => val === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), 'Contact email must be a valid email address')
    .default(''),

  contactPhone: optionalPhoneSchema.default(''),
  supportEmail: z.string()
    .refine((val) => val === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), 'Support email must be a valid email address')
    .default(''),

  // SEO Settings
  metaTitle: z.string()
    .max(60, 'SEO title must not exceed 60 characters (recommended for Google)')
    .default(''),
  metaDescription: z.string()
    .max(160, 'Meta description must not exceed 160 characters (recommended for Google)')
    .default(''),
  metaKeywords: z.string()
    .max(200, 'Meta keywords must not exceed 200 characters')
    .default(''),

  // Social Media
  facebook: z.string()
    .refine((val) => val === '' || /^https?:\/\//.test(val), 'Facebook URL must be a valid URL')
    .default(''),
  instagram: z.string()
    .refine((val) => val === '' || /^https?:\/\//.test(val), 'Instagram URL must be a valid URL')
    .default(''),
  twitter: z.string()
    .refine((val) => val === '' || /^https?:\/\//.test(val), 'Twitter URL must be a valid URL')
    .default(''),
  linkedin: z.string()
    .refine((val) => val === '' || /^https?:\/\//.test(val), 'LinkedIn URL must be a valid URL')
    .default(''),
  youtube: z.string()
    .refine((val) => val === '' || /^https?:\/\//.test(val), 'YouTube URL must be a valid URL')
    .default(''),

  // Payment Configuration
  razorpayKeyId: z.string().default(''),
  taxPercentage: z.number()
    .min(0, 'Tax percentage must be at least 0%')
    .max(100, 'Tax percentage must not exceed 100%')
    .default(18),
  currency: z.string()
    .min(1, 'Currency must be selected')
    .default('INR'),

  // Shipping Configuration
  shippingEnabled: z.boolean().default(true),
  shippingCharge: z.coerce
    .number({ message: 'Shipping charge is required' })
    .min(0, 'Shipping charge must be 0 or higher')
    .max(100000, 'Shipping charge must not exceed 100000')
    .default(0),
  freeShippingThreshold: z.preprocess(
    (val) => (val === '' || val === null || val === undefined || isNaN(Number(val)) ? null : Number(val)),
    z.number().min(0, 'Free shipping threshold must be 0 or higher').max(1000000, 'Free shipping threshold must not exceed 1000000').nullable().optional()
  ),
  shippingMessage: z.string()
    .max(500, 'Shipping message must not exceed 500 characters')
    .default(''),
}).superRefine((data, ctx) => {
  if (data.shippingEnabled) {
    if (data.shippingCharge === undefined || data.shippingCharge === null || isNaN(data.shippingCharge)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Shipping charge is required when shipping is enabled',
        path: ['shippingCharge'],
      })
    }
  }
})

type SettingsFormData = z.infer<typeof settingsValidation>

const defaultValues: SettingsFormData = {
  websiteName: '',
  tagline: '',
  contactEmail: '',
  contactPhone: '',
  supportEmail: '',
  metaTitle: '',
  metaDescription: '',
  metaKeywords: '',
  razorpayKeyId: '',
  taxPercentage: 18,
  currency: 'INR',
  shippingEnabled: true,
  shippingCharge: 49,
  freeShippingThreshold: 500,
  shippingMessage: '',
  facebook: '',
  instagram: '',
  twitter: '',
  linkedin: '',
  youtube: '',
}

export default function SettingsPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [unsavedWarningVisible, setUnsavedWarningVisible] = useState(false)
  const [nextRoute, setNextRoute] = useState<string | null>(null)

  useModalScrollLock(unsavedWarningVisible)

  const form = useForm<SettingsFormData>({
    resolver: zodResolver(settingsValidation) as any,
    mode: 'onChange',
    defaultValues,
  })

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      const apiClient = getApiClient()
      const response = await apiClient.get('/admin/settings')
      if (response.data?.data) {
        form.reset(response.data.data)
      }
    } catch (error) {
      toast.error('Failed to load settings')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (data: SettingsFormData) => {
    try {
      const apiClient = getApiClient()
      await apiClient.put('/admin/settings', data)
      form.reset(data)
      toast.success('Settings saved successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save settings')
    }
  }

  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      if (form.formState.isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    },
    [form.formState.isDirty]
  )

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [handleBeforeUnload])

  const handleNavigation = (route: string) => {
    if (form.formState.isDirty) {
      setUnsavedWarningVisible(true)
      setNextRoute(route)
    } else {
      navigate(route)
    }
  }

  const confirmNavigation = () => {
    if (nextRoute) {
      navigate(nextRoute)
    }
  }

  const charLimits: Record<string, number> = useMemo(() => ({
    websiteName: 100,
    tagline: 160,
    metaTitle: 60,
    metaDescription: 160,
    metaKeywords: 200,
    shippingMessage: 500,
  }), [])

  const FormField = useCallback(({
    label,
    name,
    type = 'text',
    placeholder,
    rows,
    options,
  }: {
    label: string
    name: keyof SettingsFormData
    type?: string
    placeholder?: string
    rows?: number
    options?: { value: string; label: string }[]
  }) => {
    const charLimit = charLimits[name as string]

    if (type === 'select' && options) {
      return (
        <div>
          <label className="block text-sm font-semibold text-[#6D3A0D] mb-2">
            {label}
          </label>
          <Controller
            name={name}
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <>
                <select
                  {...field}
                  value={String(field.value || '')}
                  className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                    error
                      ? 'border-red-500 bg-red-50 focus:border-red-600'
                      : 'border-[#6D3A0D] focus:border-[#5A2F0A]'
                  }`}
                >
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {error && (
                  <p className="text-xs text-red-600 mt-1">{error.message}</p>
                )}
              </>
            )}
          />
        </div>
      )
    }

    if (type === 'textarea') {
      return (
        <div>
          <Controller
            name={name}
            control={form.control}
            render={({ field: { value, onChange, ...fieldProps }, fieldState: { error } }) => {
              const charCount = typeof value === 'string' ? value.length : 0
              return (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-[#6D3A0D]">
                      {label}
                    </label>
                    {charLimit && (
                      <span className={`text-xs font-medium ${
                        charCount > charLimit * 0.9
                          ? 'text-red-600'
                          : charCount > charLimit * 0.75
                          ? 'text-amber-600'
                          : 'text-gray-500'
                      }`}>
                        {charCount} / {charLimit}
                      </span>
                    )}
                  </div>
                  <textarea
                    {...fieldProps}
                    value={String(value || '')}
                    onChange={(e) => onChange(e.target.value)}
                    rows={rows || 3}
                    placeholder={placeholder}
                    className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition-colors resize-none ${
                      error
                        ? 'border-red-500 bg-red-50 focus:border-red-600'
                        : 'border-[#6D3A0D] focus:border-[#5A2F0A]'
                    }`}
                  />
                  {error && (
                    <p className="text-xs text-red-600 mt-1">{error.message}</p>
                  )}
                </>
              )
            }}
          />
        </div>
      )
    }

    return (
      <Controller
        name={name}
        control={form.control}
        render={({ field: { value, onChange, ...fieldProps }, fieldState: { error } }) => {
          const charCount = typeof value === 'string' ? value.length : 0

          const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (type === 'number') {
              const numValue = e.target.value
              if (numValue === '' || isNaN(Number(numValue))) {
                onChange(0)
              } else {
                onChange(Number(numValue))
              }
            } else {
              onChange(e.target.value)
            }
          }

          return (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-[#6D3A0D]">
                  {label}
                </label>
                {charLimit && type !== 'disabled' && (
                  <span className={`text-xs font-medium ${
                    charCount > charLimit * 0.9
                      ? 'text-red-600'
                      : charCount > charLimit * 0.75
                      ? 'text-amber-600'
                      : 'text-gray-500'
                  }`}>
                    {charCount} / {charLimit}
                  </span>
                )}
              </div>
              <input
                {...fieldProps}
                value={String(value || '')}
                onChange={handleChange}
                type={type}
                placeholder={placeholder}
                disabled={type === 'disabled'}
                className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none transition-colors ${
                  type === 'disabled'
                    ? 'bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed'
                    : error
                    ? 'border-red-500 bg-red-50 focus:border-red-600'
                    : 'border-[#6D3A0D] focus:border-[#5A2F0A]'
                }`}
              />
              {error && (
                <p className="text-xs text-red-600 mt-1">{error.message}</p>
              )}
            </div>
          )
        }}
      />
    )
  }, [charLimits, form.control])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D3A0D]"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Unsaved Changes Warning */}
      {unsavedWarningVisible && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border-2 border-[#F6EEE7]">
            <h3 className="text-lg font-bold text-[#6D3A0D] mb-2">
              Unsaved Changes
            </h3>
            <p className="text-gray-600 mb-6 text-sm">
              You have unsaved changes. Are you sure you want to leave?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setUnsavedWarningVisible(false)
                  setNextRoute(null)
                }}
                className="px-4 py-2 rounded-xl border-2 border-[#6D3A0D] text-[#6D3A0D] font-bold text-sm hover:bg-[#F6EEE7] transition-colors"
              >
                Stay
              </button>
              <button
                type="button"
                onClick={confirmNavigation}
                className="px-4 py-2 rounded-xl bg-[#6D3A0D] text-white font-bold text-sm hover:bg-[#5A2F0A] transition-colors shadow-sm"
              >
                Leave
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <SettingsIcon className="w-8 h-8 text-[#6D3A0D]" />
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-[#6D3A0D]">Settings</h1>
          <p className="text-sm text-gray-600 mt-1">
            {form.formState.isDirty ? '✓ Changes detected' : 'All settings saved'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={form.handleSubmit((data) => handleSave(data as SettingsFormData))} className="space-y-8">
        {/* General Information */}
        <div className="bg-white rounded-lg border-2 border-[#F6EEE7] p-6 space-y-4">
          <div>
            <h2 className="text-xl font-bold text-[#6D3A0D] mb-1">
              General Information
            </h2>
            <p className="text-xs text-gray-600">
              Basic website settings and contact details (shown in website footer and headers)
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormField
                label="Website Name"
                name="websiteName"
                placeholder="e.g., Te Amo"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Displayed in website footer and headers</p>
            </div>
            <div>
              <FormField
                label="Tagline"
                name="tagline"
                placeholder="e.g., Natural hair and body care"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Brief description of your brand (max 160 characters)</p>
            </div>
            <div>
              <FormField
                label="Contact Email"
                name="contactEmail"
                type="email"
                placeholder="e.g., info@teamo.com"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Shown in website footer as clickable link</p>
            </div>
            <div>
              <FormField
                label="Contact Phone"
                name="contactPhone"
                type="tel"
                placeholder="e.g., 9876543210"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Indian phone number (10 digits, starting with 6-9) - displayed in website footer</p>
            </div>
            <div className="md:col-span-2">
              <FormField
                label="Support Email"
                name="supportEmail"
                type="email"
                placeholder="e.g., support@teamo.com"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Primary support email (shown in footer)</p>
            </div>
          </div>
        </div>

        {/* SEO Settings */}
        <div className="bg-white rounded-lg border-2 border-[#F6EEE7] p-6 space-y-4">
          <div>
            <h2 className="text-xl font-bold text-[#6D3A0D] mb-1">
              SEO Information
            </h2>
            <p className="text-xs text-gray-600">
              Search engine optimization settings for better search visibility
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              📌 <strong>SEO Tips:</strong> Use keywords naturally, keep title under 60 characters and description under 160. This helps Google understand and rank your site better.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <FormField
                label="SEO Title"
                name="metaTitle"
                placeholder="e.g., Natural Hair Care Products | Te Amo"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Title shown in Google search results (max 60 characters)</p>
            </div>
            <div>
              <FormField
                label="Meta Description"
                name="metaDescription"
                type="textarea"
                placeholder="e.g., Discover natural hair and body care products crafted with love. Free shipping on orders above ₹500."
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Description shown below title in search results (max 160 characters)</p>
            </div>
            <div>
              <FormField
                label="Meta Keywords"
                name="metaKeywords"
                placeholder="e.g., natural hair care, organic shampoo, body wash, eco-friendly"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Comma-separated keywords (max 200 characters)</p>
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white rounded-lg border-2 border-[#F6EEE7] p-6 space-y-4">
          <div>
            <h2 className="text-xl font-bold text-[#6D3A0D] mb-1">
              Social Media
            </h2>
            <p className="text-xs text-gray-600">
              Social media profile links (displayed in website footer)
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-amber-800">
              🔗 <strong>Optional:</strong> Add complete URLs to your social profiles. Leave empty if not applicable. Links will appear in the website footer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormField
                label="Facebook URL"
                name="facebook"
                type="url"
                placeholder="https://facebook.com/company"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Full URL to your Facebook page</p>
            </div>
            <div>
              <FormField
                label="Instagram URL"
                name="instagram"
                type="url"
                placeholder="https://instagram.com/company"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Full URL to your Instagram profile</p>
            </div>
            <div>
              <FormField
                label="Twitter/X URL"
                name="twitter"
                type="url"
                placeholder="https://x.com/company"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Full URL to your X/Twitter account</p>
            </div>
            <div>
              <FormField
                label="LinkedIn URL"
                name="linkedin"
                type="url"
                placeholder="https://linkedin.com/company/company-name"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Full URL to your LinkedIn page</p>
            </div>
            <div className="md:col-span-2">
              <FormField
                label="YouTube URL"
                name="youtube"
                type="url"
                placeholder="https://youtube.com/@company"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Full URL to your YouTube channel</p>
            </div>
          </div>
        </div>

        {/* Payment Configuration */}
        <div className="bg-white rounded-lg border-2 border-[#F6EEE7] p-6 space-y-4">
          <div>
            <h2 className="text-xl font-bold text-[#6D3A0D] mb-1">
              Payment Configuration
            </h2>
            <p className="text-xs text-gray-600">
              Payment gateway and tax settings
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3 mb-4">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800 font-semibold mb-1">🔐 Security Notice</p>
              <p className="text-sm text-blue-800">
                Razorpay credentials are configured via environment variables for security. They cannot be changed here.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormField
                label="Razorpay Key ID (Display Only)"
                name="razorpayKeyId"
                type="disabled"
                placeholder="Read from environment"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Read-only field (configure in .env)</p>
            </div>
            <div>
              <FormField
                label="Currency"
                name="currency"
                type="select"
                options={[
                  { value: 'INR', label: 'Indian Rupee (INR)' },
                  { value: 'USD', label: 'US Dollar (USD)' },
                  { value: 'EUR', label: 'Euro (EUR)' },
                ]}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Currency used for all transactions</p>
            </div>
            <div>
              <FormField
                label="Tax Percentage (%)"
                name="taxPercentage"
                type="number"
                placeholder="e.g., 18"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Tax rate added to order totals (0-100%)</p>
            </div>
          </div>
        </div>

        {/* Shipping Configuration */}
        <div className="bg-white rounded-lg border-2 border-[#F6EEE7] p-6 space-y-4">
          <div>
            <h2 className="text-xl font-bold text-[#6D3A0D] mb-1">
              Shipping Configuration
            </h2>
            <p className="text-xs text-gray-600">
              Shipping rates and delivery settings displayed to customers during checkout
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#6D3A0D] mb-3">
                Enable Shipping
              </label>
              <Controller
                name="shippingEnabled"
                control={form.control}
                render={({ field: { value, onChange } }) => (
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                      className="w-5 h-5 rounded border-2 border-[#6D3A0D] text-[#6D3A0D] cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">
                      {value ? '✓ Shipping is enabled' : '✗ Shipping is disabled'}
                    </span>
                  </div>
                )}
              />
              <p className="text-xs text-gray-500 mt-2">💡 Toggle to enable/disable shipping for your store</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FormField
                  label="Shipping Charge (₹)"
                  name="shippingCharge"
                  type="number"
                  placeholder="e.g., 49"
                />
                <p className="text-xs text-gray-500 mt-1">💡 Standard shipping cost for orders below threshold</p>
              </div>
              <div>
                <FormField
                  label="Free Shipping Threshold (₹)"
                  name="freeShippingThreshold"
                  type="number"
                  placeholder="e.g., 500"
                />
                <p className="text-xs text-gray-500 mt-1">💡 Orders above this amount get free shipping</p>
              </div>
            </div>

            {/* <div>
              <FormField
                label="Shipping Message"
                name="shippingMessage"
                type="textarea"
                placeholder="e.g., Free shipping on all orders above ₹500. For orders below ₹500, shipping charges are ₹49."
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1">💡 Message displayed to customers during checkout (max 500 characters)</p>
            </div> */}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => form.reset()}
            disabled={!form.formState.isDirty}
            className="px-6 py-2 rounded-lg border-2 border-[#6D3A0D] text-[#6D3A0D] font-medium hover:bg-[#F6EEE7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset Changes
          </button>
          <LoadingButton
            type="submit"
            state={form.formState.isSubmitting ? 'loading' : 'idle'}
            variant="primary"
            size="lg"
            disabled={
              !form.formState.isDirty || form.formState.isSubmitting
            }
            loadingText="Saving..."
            successText="Saved!"
            className="disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-5 h-5" />
            Save Changes
          </LoadingButton>
        </div>
      </form>
    </div>
  )
}
